import { ApiViolationsMaintenance } from './violationsMaintenance';
import {
  EmailSchedule,
  User,
  BoardMember,
  BoardMeeting,
  BoardResolution,
  WorkOrder,
} from '../../types';

export class ApiComms extends ApiViolationsMaintenance {
  // ============ EMAIL SCHEDULING ============

  async getEmailSchedules(condoId: string): Promise<EmailSchedule[]> {
    return this.request<EmailSchedule[]>(`/condo/email-schedules/condo/${condoId}`);
  }

  async getEmailSchedule(id: string): Promise<EmailSchedule> {
    return this.request<EmailSchedule>(`/condo/email-schedules/${id}`);
  }

  async createEmailSchedule(schedule: EmailSchedule): Promise<EmailSchedule> {
    return this.request<EmailSchedule>('/condo/email-schedules', {
      method: 'POST',
      body: JSON.stringify(schedule),
    });
  }

  async updateEmailSchedule(id: string, schedule: Partial<EmailSchedule>): Promise<EmailSchedule> {
    return this.request<EmailSchedule>(`/condo/email-schedules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(schedule),
    });
  }

  async cancelEmailSchedule(id: string): Promise<void> {
    return this.request<void>(`/condo/email-schedules/${id}/cancel`, { method: 'POST' });
  }

  async deleteEmailSchedule(id: string): Promise<void> {
    return this.request<void>(`/condo/email-schedules/${id}`, { method: 'DELETE' });
  }

  async sendEmailNow(id: string): Promise<EmailSchedule> {
    return this.request<EmailSchedule>(`/condo/email-schedules/${id}/send-now`, { method: 'POST' });
  }

  // ============ END EMAIL SCHEDULING ============

  async updateUserProfile(userData: Partial<User> & { currentPassword?: string; newPassword?: string }): Promise<User> {
    // Sprint 20 — was hand-rolling fetch + manual Authorization header,
    // which meant a 401 here would NOT trigger silent refresh and the
    // X-Request-Id was never sent. Re-routed through this.request so the
    // refresh dance + audit trail apply uniformly.
    return this.request<User>('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Sprint 123.2 — owner profile SELF-edit. Hits the real backend endpoint
   * (PUT /api/auth/user/{userId}); AuthService.updateUserProfile permits a
   * self-edit of name/email/phone for any authenticated user and forbids
   * editing anyone else (403). The caller passes ONLY their own userId — the
   * owner-home UI never exposes another user's id — so this is strictly
   * self-scoped. Returns the refreshed user.
   */
  async updateMyProfile(
    userId: string,
    fields: { name?: string; email?: string; phoneNumber?: string },
  ): Promise<User> {
    return this.request<User>(`/auth/user/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
  }

  // ============ BOARD MEMBERS ============

  async getBoardMembers(condoId?: string): Promise<BoardMember[]> {
    const url = condoId ? `/board-members/condo/${condoId}` : '/board-members';
    return this.request<BoardMember[]>(url);
  }

  async createBoardMember(member: Omit<BoardMember, 'id'>): Promise<BoardMember> {
    return this.request<BoardMember>('/board-members', { method: 'POST', body: JSON.stringify(member) });
  }

  async updateBoardMember(id: string, member: Partial<BoardMember>): Promise<BoardMember> {
    return this.request<BoardMember>(`/board-members/${id}`, { method: 'PUT', body: JSON.stringify(member) });
  }

  async deleteBoardMember(id: string): Promise<void> {
    return this.request<void>(`/board-members/${id}`, { method: 'DELETE' });
  }

  // ============ BOARD MEETINGS ============

  async getBoardMeetings(condoId?: string): Promise<BoardMeeting[]> {
    const url = condoId ? `/board-meetings/condo/${condoId}` : '/board-meetings';
    return this.request<any[]>(url).then(raw => raw.map(this.parseMeeting));
  }

  private parseMeeting(m: any): BoardMeeting {
    return {
      ...m,
      attendees: m.attendeesJson ? JSON.parse(m.attendeesJson) : (m.attendees ?? []),
      agenda:    m.agendaJson    ? JSON.parse(m.agendaJson)    : (m.agenda ?? []),
      minutes:   m.minutesJson   ? JSON.parse(m.minutesJson)   : (m.minutes ?? []),
    };
  }

  async createBoardMeeting(meeting: Omit<BoardMeeting, 'id'>): Promise<BoardMeeting> {
    const payload = {
      ...meeting,
      attendeesJson: JSON.stringify(meeting.attendees ?? []),
      agendaJson:    JSON.stringify(meeting.agenda ?? []),
      minutesJson:   JSON.stringify(meeting.minutes ?? []),
    };
    return this.request<any>('/board-meetings', { method: 'POST', body: JSON.stringify(payload) })
      .then(this.parseMeeting);
  }

  async updateBoardMeeting(id: string, meeting: Partial<BoardMeeting>): Promise<BoardMeeting> {
    const payload: any = { ...meeting };
    if (meeting.attendees !== undefined) payload.attendeesJson = JSON.stringify(meeting.attendees);
    if (meeting.agenda    !== undefined) payload.agendaJson    = JSON.stringify(meeting.agenda);
    if (meeting.minutes   !== undefined) payload.minutesJson   = JSON.stringify(meeting.minutes);
    return this.request<any>(`/board-meetings/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
      .then(this.parseMeeting);
  }

  async deleteBoardMeeting(id: string): Promise<void> {
    return this.request<void>(`/board-meetings/${id}`, { method: 'DELETE' });
  }

  // ============ BOARD RESOLUTIONS ============

  async getBoardResolutions(condoId?: string): Promise<BoardResolution[]> {
    const url = condoId ? `/board-resolutions/condo/${condoId}` : '/board-resolutions';
    return this.request<BoardResolution[]>(url);
  }

  async createBoardResolution(res: Omit<BoardResolution, 'id'>): Promise<BoardResolution> {
    return this.request<BoardResolution>('/board-resolutions', { method: 'POST', body: JSON.stringify(res) });
  }

  async updateBoardResolution(id: string, res: Partial<BoardResolution>): Promise<BoardResolution> {
    return this.request<BoardResolution>(`/board-resolutions/${id}`, { method: 'PUT', body: JSON.stringify(res) });
  }

  async closeBoardResolution(id: string): Promise<BoardResolution> {
    return this.request<BoardResolution>(`/board-resolutions/${id}/close`, { method: 'POST' });
  }

  async deleteBoardResolution(id: string): Promise<void> {
    return this.request<void>(`/board-resolutions/${id}`, { method: 'DELETE' });
  }

  // ============ WORK ORDERS ============

  async getWorkOrders(condoId?: string): Promise<WorkOrder[]> {
    const url = condoId ? `/work-orders/condo/${condoId}` : '/work-orders';
    return this.request<WorkOrder[]>(url);
  }

  async createWorkOrder(wo: Omit<WorkOrder, 'id'>): Promise<WorkOrder> {
    return this.request<WorkOrder>('/work-orders', { method: 'POST', body: JSON.stringify(wo) });
  }

  async updateWorkOrder(id: string, wo: Partial<WorkOrder>): Promise<WorkOrder> {
    return this.request<WorkOrder>(`/work-orders/${id}`, { method: 'PUT', body: JSON.stringify(wo) });
  }

  async completeWorkOrder(id: string, actualCost?: number): Promise<WorkOrder> {
    return this.request<WorkOrder>(`/work-orders/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify(actualCost !== undefined ? { actualCost } : {}),
    });
  }

  async deleteWorkOrder(id: string): Promise<void> {
    return this.request<void>(`/work-orders/${id}`, { method: 'DELETE' });
  }

  // ============ VISITOR / GUEST MANAGEMENT ============

  async getVisitorAuthorizations(condoId?: string, unitNumber?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (condoId) params.append('condoId', condoId);
    if (unitNumber) params.append('unitNumber', unitNumber);
    const qs = params.toString();
    return this.request<any[]>(`/visitor-authorizations${qs ? '?' + qs : ''}`);
  }

  async createVisitorAuthorization(dto: any): Promise<any> {
    return this.request<any>('/visitor-authorizations', { method: 'POST', body: JSON.stringify(dto) });
  }

  async updateVisitorAuthorization(id: string, dto: any): Promise<any> {
    return this.request<any>(`/visitor-authorizations/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
  }

  async deleteVisitorAuthorization(id: string): Promise<void> {
    return this.request<void>(`/visitor-authorizations/${id}`, { method: 'DELETE' });
  }

  async revokeVisitorAuthorization(id: string): Promise<any> {
    return this.request<any>(`/visitor-authorizations/${id}/revoke`, { method: 'POST' });
  }

  // ============ PARKING SPOTS ============

  async getParkingSpots(condoId?: string): Promise<any[]> {
    const qs = condoId ? `?condoId=${condoId}` : '';
    return this.request<any[]>(`/parking-spots${qs}`);
  }

  async createParkingSpot(dto: any): Promise<any> {
    return this.request<any>('/parking-spots', { method: 'POST', body: JSON.stringify(dto) });
  }

  async updateParkingSpot(id: string, dto: any): Promise<any> {
    return this.request<any>(`/parking-spots/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
  }

  async deleteParkingSpot(id: string): Promise<void> {
    return this.request<void>(`/parking-spots/${id}`, { method: 'DELETE' });
  }

  async assignParkingSpot(id: string, ownerId: string, ownerName?: string, unitNumber?: string): Promise<any> {
    const params = new URLSearchParams({ ownerId });
    if (ownerName)   params.append('ownerName', ownerName);
    if (unitNumber)  params.append('unitNumber', unitNumber);
    return this.request<any>(`/parking-spots/${id}/assign?${params.toString()}`, { method: 'PUT' });
  }

  async unassignParkingSpot(id: string): Promise<any> {
    return this.request<any>(`/parking-spots/${id}/unassign`, { method: 'PUT' });
  }

  // ============ STATUS CERTIFICATES ============

  async getStatusCertificates(condoId?: string): Promise<any[]> {
    const qs = condoId ? `?condoId=${condoId}` : '';
    return this.request<any[]>(`/status-certificates${qs}`);
  }

  async createStatusCertificate(dto: any): Promise<any> {
    return this.request<any>('/status-certificates', { method: 'POST', body: JSON.stringify(dto) });
  }

  /**
   * Sprint 129 — authoritative issuance. POSTs the complete certificate content;
   * the server renders + persists the PDF and returns the created DTO (with the
   * server-assigned number + id). The client then downloads the rendered PDF via
   * GET /status-certificates/{id}/pdf.
   */
  async issueStatusCertificate(condoId: string, content: any, requestId?: string): Promise<any> {
    // Sprint 131 — when issued against a request, pass requestId so the server
    // links the certificate and stamps the request fulfilled.
    const rq = requestId ? `&requestId=${requestId}` : '';
    return this.request<any>(`/status-certificates/issue?condoId=${condoId}${rq}`, {
      method: 'POST', body: JSON.stringify(content),
    });
  }

  async deleteStatusCertificate(id: string): Promise<void> {
    return this.request<void>(`/status-certificates/${id}`, { method: 'DELETE' });
  }

  // ============ STATUS CERTIFICATE REQUESTS ============

  async getStatusCertificateRequests(condoId: string): Promise<any[]> {
    return this.request<any[]>(`/status-certificate-requests?condoId=${condoId}`);
  }

  async createStatusCertificateRequest(condoId: string, body: any): Promise<any> {
    return this.request<any>(`/status-certificate-requests?condoId=${condoId}`, {
      method: 'POST', body: JSON.stringify(body),
    });
  }

  async cancelStatusCertificateRequest(id: string, reason?: string): Promise<any> {
    return this.request<any>(`/status-certificate-requests/${id}/cancel`, {
      method: 'POST', body: JSON.stringify({ reason }),
    });
  }
}
